import React from "react"
import { z } from "zod"

import { Button, Dialog, Form, FormInput, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { ColorPicker } from "@/components/color-picker"
import { mutateOk, useZero } from "@/lib/zero"

interface UpdateTagGroupDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	values: UpdateTagGroupFormValues | null
}

const UpdateTagGroupFormSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	color: z.string().min(1),
})
type UpdateTagGroupFormValues = z.infer<typeof UpdateTagGroupFormSchema>

export const UpdateTagGroupDialog = React.memo(
	({ isOpen, onOpenChange, values }: UpdateTagGroupDialogProps) => {
		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				title="Update group"
				footer={
					<Button form="update-tag-group-form" type="submit">
						Update
					</Button>
				}
			>
				{values && <Inner values={values} onOpenChange={onOpenChange} />}
			</Dialog>
		)
	},
)

type InnerProps = Pick<UpdateTagGroupDialogProps, "values" | "onOpenChange">

const Inner = React.memo(({ values, onOpenChange }: InnerProps) => {
	const zero = useZero()
	const form = useZodForm({
		schema: UpdateTagGroupFormSchema,
		defaultValues: {
			...values,
		},
	})
	const colorWatch = form.watch("color")

	return (
		<Form
			id="update-tag-group-form"
			form={form}
			className="space-y-4"
			onSubmit={async (values) => {
				await mutateOk(
					zero.mutate(
						mutators.tagGroups.update({
							id: values.id,
							color: values.color,
							name: values.name,
						}),
					),
				)
				onOpenChange(false)
			}}
		>
			<div className="flex items-end gap-4">
				<FormInput name="name" label="Name" className="flex-1" />
				<ColorPicker
					value={colorWatch}
					onChange={(color) => {
						form.setValue("color", color)
					}}
					withInputs={false}
				/>
			</div>
		</Form>
	)
})
